import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Sparkles } from 'lucide-react';
import api from '../../lib/api';

/**
 * Community "Field-Team Wall" — recent AR photos explorers have posted
 * to the Lodge. Public feed, no auth required, hits /api/ar/lodge-feed.
 */
export default function FieldTeamWall() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let dead = false;
    api.get('/ar/lodge-feed', { params: { limit: 24 } })
      .then(({ data }) => { if (!dead) setPhotos(data || []); })
      .catch(() => {})
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  if (loading) return null;
  if (photos.length === 0) return null; // hide until at least one post exists

  return (
    <section className="mb-8" data-testid="lodge-field-team-wall">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Field-Team Wall</p>
        <p className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> {photos.length} shared
        </p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {photos.map((p, i) => (
          <motion.button
            key={p.photo_id}
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i, 12) * 0.02 }}
            onClick={() => setActive(p)}
            data-testid={`wall-photo-${i}`}
            className="relative aspect-square rounded-xl overflow-hidden ring-1 ring-white/10 hover:ring-emerald-400/60 transition"
            aria-label={`View ${p.species_name || 'field-team'} photo by ${p.owner_name}`}
          >
            <img src={p.picture} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
            <div className="absolute inset-x-1.5 bottom-1.5">
              <p className="text-[9px] uppercase tracking-widest text-emerald-200 truncate">{p.owner_name || 'Explorer'}</p>
              <p className="text-[10px] font-serif leading-tight line-clamp-1 text-white">{p.species_name || 'Wildlife'}</p>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex flex-col"
            data-testid="wall-lightbox"
            onClick={() => setActive(null)}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setActive(null); }}
              data-testid="wall-lightbox-close"
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 z-10"
              style={{ top: 'max(env(safe-area-inset-top), 1rem)' }}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
              <img src={active.picture} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl" />
            </div>
            <div className="p-4 pb-[max(env(safe-area-inset-bottom),1rem)] text-center" onClick={(e) => e.stopPropagation()}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 flex items-center justify-center gap-1.5">
                <Camera className="w-3 h-3" /> Field-Team
              </p>
              <p className="font-serif text-lg text-white mt-1">{active.species_name || 'Wildlife'}</p>
              <p className="text-[11px] text-white/60 mt-0.5">
                by {active.owner_name || 'Explorer'} · {new Date(active.posted_to_lodge_at || active.created_at).toLocaleDateString(undefined, { day:'numeric', month:'short' })}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
